<template>
  <div class="recorder">
    <view-finder
      ref="finder"
      :state="finderState"
      :caption="caption"
      :guide="guideState"
      :has-shot="phase === 'done' && !playbackUrl"
      :class="{ 'has-video': !!playbackUrl, 'need-unmute': needsUnmute }"
      @click.native="onFrameClick"
    >
      <video ref="camera" playsinline muted autoplay></video>
      <img v-if="poster" class="shot" :src="poster" alt="" />
      <video ref="playback" class="playback" playsinline></video>

      <template #overlay>
        <div v-if="phase === 'recording'" class="live rec" aria-live="polite">
          <span class="dot"></span>در حال ضبط · <b>{{ faSecondsLeft }}</b> ثانیه
        </div>

        <button
          v-if="playbackUrl"
          type="button"
          class="vtoggle"
          :data-paused="paused ? '' : null"
          :aria-label="paused ? 'پخش ویدیو' : 'توقف ویدیو'"
          @click.stop="toggle"
        >
          <base-icon :name="paused ? 'play' : 'pause'" />
          <span>{{ paused ? "پخش" : "توقف" }}</span>
        </button>

        <button v-if="needsUnmute" type="button" class="unmute" @click.stop="unmute">
          <base-icon name="volume" />
          <span>شنیدن صدا</span>
        </button>
      </template>
    </view-finder>
  </div>
</template>

<script>
import ViewFinder from "./ViewFinder.vue";
import BaseIcon from "./BaseIcon.vue";
import {
  captureFrame,
  createRecorder,
  RECORDING_VIDEO_CONSTRAINTS,
  RECORDING_AUDIO_CONSTRAINTS,
} from "@/utils/media";
import { toFa } from "@/utils/digits";
import { canRecord, canUseCamera } from "@/utils/device";
import { createFramingWatch } from "@/utils/face";
import { RECORD_SECONDS, RECORD_MIN_SECONDS } from "@/constants";

/**
 * Records the spoken/head-turn clip, then plays it back for review.
 *
 * Three phases: `idle` (live preview), `recording` (countdown running), `done` (playback).
 * The parent drives transitions with `start()` / `stop()` / `reset()` and renders its own
 * footer buttons — this component owns only the hardware and the frame.
 *
 * The recorder and the stream are instance fields, never `data`: they are large objects
 * that nothing renders from, and making them reactive would have Vue walk them on every
 * change.
 */
export default {
  name: "VideoRecorder",
  components: { ViewFinder, BaseIcon },

  props: {
    active: { type: Boolean, default: false },
    seconds: { type: Number, default: RECORD_SECONDS },
    minSeconds: { type: Number, default: RECORD_MIN_SECONDS },
    /** Check the face is framed before recording starts, and report it as `framed`. */
    guide: { type: Boolean, default: false },
  },

  data() {
    return {
      /** "idle" | "recording" | "done" */
      phase: "idle",
      secondsLeft: this.seconds,
      poster: "",
      playbackUrl: "",
      paused: false,
      needsUnmute: false,
      /** Whether a live stream is attached — drives the frame's idle/live look. */
      streamReady: false,
      /** "searching" | "adjust" | "ok" — the latest framing verdict. */
      framing: "searching",
      framingHint: "",
      framed: false,
    };
  },

  computed: {
    finderState() {
      if (this.phase === "done") return "captured";
      return this.streamReady ? "live" : "idle";
    },

    /**
     * Framing is checked before recording, never during it.
     *
     * This step asks the user to turn their head, so a face leaving the oval mid-clip is
     * the instruction being followed — telling them off for it, or cutting the recording,
     * would be the check working against the task.
     */
    tracking() {
      return this.guide && this.phase === "idle" && this.finderState === "live";
    },

    guideState() {
      if (!this.tracking) return "off";
      if (this.framed) return "ok";
      // A good frame that has not settled is not yet a green light — the button is still
      // disabled, and the ring must not say otherwise.
      return this.framing === "ok" ? "adjust" : this.framing;
    },

    caption() {
      if (this.phase === "done") return "ویدیو ثبت شد";
      return this.tracking ? this.framingHint : "";
    },
    faSecondsLeft() {
      return toFa(Math.max(0, this.secondsLeft));
    },
    /**
     * How much longer saving stays locked. A clip cut off in the first seconds is too
     * short to verify, and the user would only find that out at the end of the flow.
     */
    lockRemaining() {
      if (this.phase !== "recording") return 0;
      const elapsed = this.seconds - this.secondsLeft;
      return Math.max(0, this.minSeconds - elapsed);
    },
  },

  watch: {
    active: {
      immediate: true,
      handler(isActive) {
        if (isActive) this.acquire();
        else this.release();
      },
    },
    lockRemaining(value) {
      this.$emit("lock", value);
    },
    phase: {
      immediate: true,
      handler(value) {
        this.$emit("phase", value);
      },
    },
    tracking(on) {
      if (on) this.startTracking();
      else this.stopTracking();
    },
  },

  created() {
    this.stream = null;
    this.recorder = null;
    this.chunks = [];
    this.blob = null;
    this.tick = null;
    this.pending = null;
    this.watch = createFramingWatch({
      getVideo: () => this.$refs.camera,
      getGeometry: () => this.measure(),
      onChange: this.onFraming,
      onUnavailable: () => this.$emit("unavailable"),
    });
    // Acquisition is async and can outlive the component; this lets it bail out instead
    // of attaching a stream to a torn-down instance (and leaking the camera).
    this.destroyed = false;
  },

  mounted() {
    // Not an `immediate` watcher: those run inside initState, before `created()` has built
    // `this.watch`, so the very first call threw a TypeError on every mount.
    if (this.tracking) this.startTracking();

    const playback = this.$refs.playback;
    if (!playback) return;
    // Keep the transport pill in sync with playback state however it changed — the pill,
    // a tap on the frame, or the clip simply ending.
    ["play", "pause", "ended"].forEach((event) => playback.addEventListener(event, this.syncPaused));
    this.syncPaused();
  },

  beforeDestroy() {
    this.destroyed = true;
    this.stopTracking();
    this.stopTimer();
    this.stopRecorder();
    this.revokePlayback();
    this.release();
  },

  methods: {
    startTracking() {
      this.watch.start();
    },

    stopTracking() {
      this.watch.stop();
    },

    /**
     * Where the guide oval is, read fresh each tick.
     *
     * Not cached: the frame is `flex: 1`, so anything that changes the layout above it
     * moves the oval — the video step's challenge phrase arriving from the network is
     * exactly that, and a stale rectangle miscalibrates every verdict after it. Two
     * getBoundingClientRect calls at 8 fps cost nothing next to the inference.
     */
    measure() {
      const finder = this.$refs.finder;
      return finder ? finder.geometry() : null;
    },

    onFraming(state) {
      this.framing = state.status;
      this.framingHint = state.hint;
      this.$emit("framing", state);
      if (state.framed !== this.framed) {
        this.framed = state.framed;
        this.$emit("framed", state.framed);
      }
    },

    /**
     * Acquire a stream for recording.
     *
     * Two mobile realities are handled here. First, a camera stopped moments ago may not
     * have been released by the hardware yet, so re-opening it immediately yields a black
     * feed — hence the short settle delay. Second, a device with no working microphone
     * would fail an `audio: true` request outright and blank the preview, so the
     * constraints loosen step by step rather than giving up.
     */
    async acquire() {
      // Coalesce concurrent calls: the parent flips `active` (watcher) and may also call
      // this directly on retry. Two in-flight requests would both open the camera, and the
      // loser's tracks would never be stopped.
      if (this.pending) return this.pending;
      if (this.stream) {
        this.attachPreview();
        return null;
      }
      if (!canUseCamera()) {
        this.$emit("denied");
        return null;
      }

      this.pending = (async () => {
        try {
          await new Promise((resolve) => setTimeout(resolve, 350));
          if (this.destroyed || !this.active) return;

          // Capped size and frame rate first (see RECORDING_VIDEO_CONSTRAINTS) — a smaller
          // stream is a smaller clip, and the upload is what has been timing out. The
          // uncapped attempts stay below it: the caps are all `ideal` and should never be
          // what fails a request, but if some browser refuses the shape of the object
          // outright, recording at the old size still beats not recording.
          const attempts = [
            {
              video: { facingMode: "user", ...RECORDING_VIDEO_CONSTRAINTS },
              audio: RECORDING_AUDIO_CONSTRAINTS,
            },
            { video: { ...RECORDING_VIDEO_CONSTRAINTS }, audio: RECORDING_AUDIO_CONSTRAINTS },
            { video: { facingMode: "user" }, audio: true },
            { video: true, audio: true },
            { video: true },
          ];

          let stream = null;
          for (const constraints of attempts) {
            try {
              stream = await navigator.mediaDevices.getUserMedia(constraints);
              break;
            } catch (e) {
              stream = null;
            }
          }

          if (!stream) {
            this.$emit("denied");
            return;
          }
          if (this.destroyed || !this.active) {
            stream.getTracks().forEach((track) => track.stop());
            return;
          }

          this.stream = stream;
          this.streamReady = true;
          await this.attachPreview();
          this.$emit("ready");
        } finally {
          this.pending = null;
        }
      })();

      return this.pending;
    },

    /**
     * Attach the preview and wait for a decodable frame.
     *
     * `ready` has to mean "recording will produce something": the poster frame is grabbed
     * with `captureFrame()`, which returns null while `videoWidth` is still 0.
     */
    attachPreview() {
      const camera = this.$refs.camera;
      if (!camera || !this.stream) return Promise.resolve();

      camera.srcObject = this.stream;
      const playing = camera.play();
      if (playing && playing.catch) playing.catch(() => {});

      if (camera.videoWidth) return Promise.resolve();
      return new Promise((resolve) => {
        const done = () => {
          camera.removeEventListener("loadedmetadata", done);
          resolve();
        };
        camera.addEventListener("loadedmetadata", done);
        setTimeout(done, 4000);
      });
    },

    release() {
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }
      this.streamReady = false;
      const camera = this.$refs.camera;
      if (camera) camera.srcObject = null;
    },

    /** Begin recording, or report that this browser cannot. */
    start() {
      if (!this.stream || !canRecord()) {
        this.$emit("denied");
        return;
      }

      this.chunks = [];
      this.blob = null;
      this.poster = "";
      this.revokePlayback();
      this.phase = "recording";
      this.secondsLeft = this.seconds;

      try {
        this.recorder = createRecorder(this.stream);
        this.recorder.ondataavailable = (event) => {
          if (event.data && event.data.size) this.chunks.push(event.data);
        };
        this.recorder.onstop = this.buildPlayback;
        this.recorder.start();
      } catch (e) {
        this.recorder = null;
        this.phase = "idle";
        this.$emit("denied", e);
        return;
      }

      this.$emit("recording");
      this.tick = setInterval(() => {
        this.secondsLeft -= 1;
        if (this.secondsLeft <= 0) this.stop();
      }, 1000);
    },

    /** Finish recording: freeze a poster frame, then stop the recorder. */
    stop() {
      if (this.phase !== "recording") return;
      this.stopTimer();
      // Grab the poster before the tracks go quiet — afterwards there is no frame left.
      this.poster = captureFrame(this.$refs.camera) || "";
      this.stopRecorder();
      this.phase = "done";
    },

    /** Discard the take and go back to a live preview. */
    reset() {
      this.stopTimer();
      this.stopRecorder();
      this.revokePlayback();
      this.chunks = [];
      this.blob = null;
      this.poster = "";
      this.paused = false;
      this.needsUnmute = false;
      this.secondsLeft = this.seconds;
      this.phase = "idle";
      this.attachPreview();
    },

    stopTimer() {
      if (this.tick) {
        clearInterval(this.tick);
        this.tick = null;
      }
    },

    stopRecorder() {
      if (this.recorder && this.recorder.state !== "inactive") {
        try {
          this.recorder.stop();
        } catch (e) {
          /* already stopping */
        }
      }
    },

    revokePlayback() {
      if (this.playbackUrl) {
        URL.revokeObjectURL(this.playbackUrl);
        this.playbackUrl = "";
      }
      const playback = this.$refs.playback;
      if (playback) {
        try {
          playback.pause();
        } catch (e) {
          /* not playing */
        }
        playback.removeAttribute("src");
      }
    },

    /** Recorder has flushed: assemble the blob and start reviewing it. */
    buildPlayback() {
      if (!this.chunks.length) return;

      this.blob = new Blob(this.chunks, {
        type: (this.recorder && this.recorder.mimeType) || "video/webm",
      });
      this.playbackUrl = URL.createObjectURL(this.blob);
      this.$emit("complete", { blob: this.blob, poster: this.poster });

      this.$nextTick(() => {
        const playback = this.$refs.playback;
        if (!playback) return;
        playback.src = this.playbackUrl;
        playback.muted = false; // review it with sound — that is half of what is checked
        const playing = playback.play();
        if (playing && playing.catch) {
          playing.catch(() => {
            // Sound-autoplay refused: play silently and offer a one-tap unmute rather
            // than showing a frozen frame.
            playback.muted = true;
            playback.play().catch(() => {});
            this.needsUnmute = true;
          });
        }
      });
    },

    toggle() {
      const playback = this.$refs.playback;
      if (!playback || !this.playbackUrl) return;
      if (playback.paused || playback.ended) {
        const playing = playback.play();
        if (playing && playing.catch) playing.catch(() => {});
      } else {
        playback.pause();
      }
    },

    /** The whole frame is a tap target, which sidesteps the overlays sitting above it. */
    onFrameClick(event) {
      if (!this.playbackUrl) return;
      if (event.target.closest && event.target.closest("button")) return;
      this.toggle();
    },

    unmute() {
      const playback = this.$refs.playback;
      if (playback) {
        playback.muted = false;
        const playing = playback.play();
        if (playing && playing.catch) playing.catch(() => {});
      }
      this.needsUnmute = false;
    },

    syncPaused() {
      const playback = this.$refs.playback;
      if (playback) this.paused = playback.paused || playback.ended;
    },
  },
};
</script>

<style scoped>
.recorder {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
</style>

<style>
/* Transport controls live inside ViewFinder's overlay slot, so they belong to this
   component's markup but sit under ViewFinder's `.finder` root — not reachable by a
   scoped selector from either side. */
.finder .vtoggle,
.finder .unmute {
  position: absolute;
  bottom: 14px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: rgba(8, 10, 16, 0.74);
  color: #fff;
  border: 0;
  border-radius: 999px;
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;
  backdrop-filter: blur(6px);
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;
}

.finder .vtoggle {
  padding: 10px 18px;
  font-size: 13.5px;
}

.finder .unmute {
  padding: 9px 15px;
  font-size: 13px;
}

.finder .vtoggle:active {
  transform: translateX(-50%) scale(0.97);
}

.finder .vtoggle svg,
.finder .unmute svg {
  width: 16px;
  height: 16px;
  flex: 0 0 auto;
}

/* Both pills sit bottom-centre, so the unmute one steps up when they coexist. */
.finder.has-video.need-unmute .unmute {
  bottom: 66px;
}
</style>
